import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import { BlogCardSkeleton } from '../components/SkeletonLoader';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/pages/SearchResultsPage.css';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    if (query) {
      setSearchQuery(query);
      fetchSearchResults(query);
    } else {
      navigate('/blog');
    }
  }, [query]);

  const fetchSearchResults = async (searchTerm) => {
    try {
      setLoading(true);
      const response = await api.getBlogPosts({
        page: 1,
        limit: 50,
        skip: 0,
        search: searchTerm
      });

      if (response.success && response.data) {
        setSearchResults(response.data);
        setTotalResults(response.pagination?.total || response.data.length);
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      toast.error('Failed to load search results');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <>
      <SEO
        title={`Search Results for "${query}" | Supernova Lab`}
        description={`Search results for ${query} on Supernova Lab`}
        keywords="search, blog, articles, lab notes"
      />

      <main className="search-results-page">
        {/* HEADER WITH SEARCH */}
        <motion.section
          className="search-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <div>
                <Link to="/blog" className="back-to-blog">
                  <i className="fas fa-arrow-left me-2"></i> Back to Lab Notes
                </Link>
                <h1 className="search-results-title mt-3">
                  Search Results for "{query}"
                </h1>
                <p className="search-results-count">
                  {loading ? 'Searching...' : `${totalResults} result${totalResults !== 1 ? 's' : ''} found`}
                </p>
              </div>
              
              {/* Search Box */}
              <form onSubmit={handleSearch} className="search-form-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-btn">
                  <i className="fas fa-search"></i>
                </button>
              </form>
            </div>
            <div className="search-divider"></div>
          </div>
        </motion.section>

        {/* SEARCH RESULTS GRID - 3 COLUMNS */}
        <section className="search-results-section py-5">
          <div className="container">
            {loading ? (
              <div className="row g-4">
                {Array(6).fill(0).map((_, index) => (
                  <div key={index} className="col-lg-4 col-md-6 col-12">
                    <BlogCardSkeleton />
                  </div>
                ))}
              </div>
            ) : searchResults.length > 0 ? (
              <div className="row g-4">
                {searchResults.map((article) => {
                  const articleDate = new Date(article.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });

                  return (
                    <motion.div
                      key={article.id}
                      className="col-lg-4 col-md-6 col-12"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Link to={`/blog/${article.slug}`} className="search-article-card-link">
                        <div className="search-article-card">
                          <div className="search-article-image">
                            <SafeImage
                              src={article.image}
                              alt={article.title}
                              width={400}
                              height={250}
                              placeholderText="Article"
                            />
                          </div>
                          <div className="search-article-content">
                            <span className="search-article-category">{article.category_name}</span>
                            <h4 className="search-article-title">{article.title}</h4>
                            <p className="search-article-description">{article.description}</p>
                            <p className="search-article-date">{articleDate}</p>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="no-results-container">
                <i className="fas fa-search no-results-icon"></i>
                <h3 className="no-results-title">No results found</h3>
                <p className="no-results-text">
                  We couldn't find any articles matching "{query}". Try different keywords or browse all articles.
                </p>
                <Link to="/blog" className="btn btn-primary mt-3">
                  Browse All Articles
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default SearchResultsPage;